import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingActions from '../../components/feature/FloatingActions';
import AnimatedSection from '../../components/base/AnimatedSection';

const HomePage = () => {
  const [counts, setCounts] = useState({ years: 0, tests: 0, accuracy: 0 });

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = { years: 15, tests: 50, accuracy: 99.8 };
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounts({
        years: Math.floor(targets.years * progress),
        tests: Math.floor(targets.tests * progress),
        accuracy: parseFloat((targets.accuracy * progress).toFixed(1)),
      });

      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const services = [
    {
      icon: 'ri-tooth-line',
      title: 'Dental Crowns & Bridges',
      description: 'Premium quality crowns and bridges crafted with precision using advanced materials like zirconia, porcelain, and metal alloys for natural aesthetics.',
    },
    {
      icon: 'ri-contrast-drop-2-line',
      title: 'Dental Implants',
      description: 'Custom implant solutions including abutments, crowns, and full-arch restorations designed for optimal fit and long-lasting durability.',
    },
    {
      icon: 'ri-service-line',
      title: 'Dentures & Partials',
      description: 'Complete and partial dentures with superior fit and natural appearance, crafted using the latest digital design technology.',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Veneers & Cosmetics',
      description: 'Ultra-thin porcelain veneers and cosmetic restorations for smile makeovers with exceptional aesthetics and strength.',
    },
    {
      icon: 'ri-align-center',
      title: 'Orthodontic Appliances',
      description: 'Custom orthodontic devices including retainers, aligners, and appliances manufactured with precision for optimal treatment outcomes.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Assurance',
      description: 'Rigorous quality control protocols, ISO certification, and continuous monitoring to ensure the highest accuracy standards.',
    },
  ];

  const features = [
    {
      icon: 'ri-award-line',
      title: 'ISO 9001 Certified',
      description: 'Internationally recognized quality management system ensuring consistent, high-quality service delivery.',
    },
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround',
      description: 'Express service options with delivery within 3-5 days for standard cases and rush service available for urgent needs.',
    },
    {
      icon: 'ri-team-line',
      title: 'Expert Technicians',
      description: 'Certified dental technicians and specialists dedicated to precision craftsmanship and attention to detail.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '24/7 Support',
      description: 'Round-the-clock customer service and technical support for all your dental laboratory needs.',
    },
  ];

  const products = [
    {
      image: 'https://public.readdy.ai/ai/img_res/ee6b6a94ca089a5103b08ec35c876295.jpg',
      category: 'Dental Crowns',
      name: 'Premium Zirconia Crowns',
      description: 'High-quality zirconia crowns with natural aesthetics and superior durability for long-lasting restorations.',
      price: 'Custom Quote',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=dental%20bridge%20prosthetic%20restoration%20on%20clean%20white%20laboratory%20background%20professional%20product%20photography%20modern%20dental%20technology%20high%20quality%20detailed%20craftsmanship&width=420&height=300&seq=prod2&orientation=landscape',
      category: 'Dental Bridges',
      name: 'Fixed Dental Bridges',
      description: 'Custom-designed bridges for seamless tooth replacement with excellent fit and natural appearance.',
      price: 'Custom Quote',
    },
    {
      image: 'https://readdy.ai/api/search-image?query=dental%20implant%20abutment%20and%20crown%20on%20clean%20white%20medical%20background%20professional%20product%20photography%20dental%20laboratory%20equipment%20high%20quality%20organized%20display&width=420&height=300&seq=prod3&orientation=landscape',
      category: 'Implant Solutions',
      name: 'Implant Restorations',
      description: 'Complete implant solutions including custom abutments and crowns for optimal function and aesthetics.',
      price: 'Custom Quote',
    },
  ];

  const certifications = [
    { name: 'ISO 9001', icon: 'ri-award-line' },
    { name: 'FDA Approved', icon: 'ri-shield-check-line' },
    { name: 'CAP Certified', icon: 'ri-medal-line' },
    { name: 'CLIA Licensed', icon: 'ri-verified-badge-line' },
    { name: 'HIPAA Compliant', icon: 'ri-lock-line' },
    { name: 'GMP Certified', icon: 'ri-checkbox-circle-line' },
  ];

  const testimonials = [
    {
      name: 'Dr. Sarah Johnson',
      role: 'Cosmetic Dentist, Smile Design Clinic',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20dentist%20portrait%20smiling%20confident%20dental%20professional%20clean%20white%20background%20high%20quality%20headshot&width=56&height=56&seq=test1&orientation=squarish',
      rating: 5,
      text: 'Carver Dental Laboratory has been our trusted partner for over 5 years. Their craftsmanship, attention to detail, and fast turnaround times are exceptional. My patients are always thrilled with the results.',
    },
    {
      name: 'Dr. Michael Chen',
      role: 'Prosthodontist, Advanced Dental Care',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20male%20dentist%20portrait%20smiling%20confident%20dental%20professional%20clean%20white%20background%20high%20quality%20headshot&width=56&height=56&seq=test2&orientation=squarish',
      rating: 5,
      text: 'The quality of restorations from Carver Dental Laboratory is outstanding. Their use of advanced materials and digital technology ensures perfect fit and natural aesthetics every time.',
    },
    {
      name: 'Dr. Emily Rodriguez',
      role: 'General Dentist, Family Dental Practice',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20dentist%20portrait%20smiling%20confident%20dental%20professional%20clean%20white%20background%20high%20quality%20headshot&width=56&height=56&seq=test3&orientation=squarish',
      rating: 5,
      text: 'Outstanding service and reliability. The team at Carver Dental Laboratory is always responsive and delivers high-quality work on time. Highly recommended for any dental practice!',
    },
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FloatingActions />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=professional%20dental%20laboratory%20interior%20workspace%20with%20advanced%20manufacturing%20equipment%20dental%20prosthetics%20production%20area%20clean%20organized%20technical%20environment%20with%20precision%20tools%20and%20materials%20modern%20dental%20lab%20facility&width=1920&height=1080&seq=hero-lab-workspace-001&orientation=landscape)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-darkgray-700/70 via-darkgray-600/60 to-darkgray-700/70"></div>
        </div>

        {/* Floating Dental Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <i className="ri-tooth-line absolute top-20 left-10 text-white/10 text-8xl animate-float"></i>
          <i className="ri-contrast-drop-2-line absolute top-40 right-20 text-white/10 text-7xl animate-float-slow"></i>
          <i className="ri-service-line absolute bottom-32 left-1/4 text-white/10 text-9xl animate-float"></i>
          <i className="ri-flashlight-line absolute bottom-20 right-1/3 text-white/10 text-8xl animate-float-slow"></i>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <i className="ri-award-line text-gold-400"></i>
                <span className="text-sm font-semibold">ISO 9001 Certified Dental Laboratory</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-display font-bold leading-tight mb-6">
                Premium Dental
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-white">
                  Laboratory Solutions
                </span>
              </h1>

              <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
                Delivering exceptional dental prosthetics and restorations with cutting-edge technology and expert craftsmanship.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/services"
                  className="group px-8 py-4 bg-white text-gold-600 rounded-lg font-semibold hover:bg-gold-50 transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center space-x-2 whitespace-nowrap"
                >
                  <span>Explore Services</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gold-600 transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  <span>Contact Us</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce-slow">
          <i className="ri-arrow-down-line text-white text-3xl"></i>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-2xl">
                <i className="ri-time-line text-gold-600 text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-gold-600 mb-1">{counts.years}+</div>
              <div className="text-sm text-darkgray-500">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-2xl">
                <i className="ri-tooth-line text-gold-600 text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-gold-600 mb-1">{counts.tests}K+</div>
              <div className="text-sm text-darkgray-500">Cases Monthly</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-2xl">
                <i className="ri-24-hours-line text-gold-600 text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-gold-600 mb-1">24/7</div>
              <div className="text-sm text-darkgray-500">Service</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-2xl">
                <i className="ri-checkbox-circle-line text-gold-600 text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-gold-600 mb-1">{counts.accuracy}%</div>
              <div className="text-sm text-darkgray-500">Accuracy</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-2xl">
                <i className="ri-shield-check-line text-gold-600 text-3xl"></i>
              </div>
              <div className="text-4xl font-bold text-gold-600 mb-1">FDA</div>
              <div className="text-sm text-darkgray-500">Approved</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-primary-50 text-gold-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
              Our Services
            </div>
            <h2 className="text-5xl lg:text-6xl font-display font-bold text-darkgray-500 mb-6">
              Comprehensive Dental Laboratory Solutions
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-gold-500 to-gold-700 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-full">
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-primary-100 to-gold-100 rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <i className={`${service.icon} text-gold-600 text-4xl`}></i>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-darkgray-500 mb-4">{service.title}</h3>
                  <p className="text-darkgray-500 leading-relaxed mb-6">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center space-x-2 text-gold-600 font-semibold group-hover:text-gold-700 transition-colors duration-300"
                  >
                    <span>Learn More</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-gold-500 to-gold-700 transform -skew-y-12"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-lg">
                <div className="w-2 h-2 bg-gold-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-darkgray-500">Trusted by 500+ Dental Professionals</span>
              </div>
              
              <h2 className="text-5xl lg:text-6xl font-display font-bold text-darkgray-500 mb-6 leading-tight">
                Why Choose <span className="text-gold-600">Carver Dental Laboratory</span>
              </h2>
              
              <p className="text-lg text-darkgray-500 mb-8 leading-relaxed">
                We combine cutting-edge technology with expert craftsmanship to deliver exceptional dental prosthetics. Our commitment to quality, precision, and timely delivery makes us the preferred choice for dental professionals.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: 'ri-award-line',
                    title: 'Premium Quality',
                    description: 'ISO certified processes ensuring the highest standards in every restoration'
                  },
                  {
                    icon: 'ri-time-line',
                    title: 'Fast Turnaround',
                    description: 'Express services available with 3-5 day standard delivery on most cases'
                  },
                  {
                    icon: 'ri-microscope-line',
                    title: 'Advanced Technology',
                    description: 'State-of-the-art CAD/CAM systems and digital workflow integration'
                  },
                  {
                    icon: 'ri-customer-service-2-line',
                    title: '24/7 Support',
                    description: 'Dedicated technical support team available whenever you need assistance'
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                    className="flex items-start space-x-4 group"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-gold-500 to-gold-700 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${feature.icon} text-white text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-darkgray-500 mb-1">{feature.title}</h3>
                      <p className="text-darkgray-500">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="mt-10"
              >
                <Link
                  to="/about"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
                >
                  <span>Learn More About Us</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Column - Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20dental%20laboratory%20technician%20working%20with%20advanced%20CAD%20CAM%20technology%20and%20digital%20equipment%20in%20a%20clean%20professional%20white%20laboratory%20environment%20with%20golden%20accent%20lighting%20creating%20precise%20dental%20restorations%20manufacturing%20workspace&width=600&height=700&seq=whychooseus-lab-tech-002&orientation=portrait"
                  alt="Dental Laboratory Technology"
                  className="w-full h-auto rounded-3xl shadow-2xl object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-gold-500 to-gold-700 rounded-xl">
                      <i className="ri-shield-check-line text-white text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-darkgray-500">99.8%</p>
                      <p className="text-sm text-darkgray-500">Quality Rate</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Products Showcase */}
      <AnimatedSection className="py-24 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-2 bg-primary-50 text-gold-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
              Our Products
            </div>
            <h2 className="text-5xl lg:text-6xl font-display font-bold text-darkgray-500 mb-6">
              Premium Dental Products & Solutions
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-gold-500 to-gold-700 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="relative h-72 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gold-600 rounded-full text-sm font-semibold">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-display font-bold text-darkgray-500 mb-3">{product.name}</h3>
                    <p className="text-darkgray-500 mb-6 leading-relaxed">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-gold-600">{product.price}</span>
                      <Link
                        to="/quote"
                        className="px-6 py-2 border-2 border-gold-500 text-gold-600 rounded-lg font-semibold hover:bg-gold-500 hover:text-white transition-all duration-300 whitespace-nowrap"
                      >
                        Request Quote
                      </Link>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white border-2 border-gold-500 text-gold-600 rounded-lg font-semibold hover:bg-gold-500 hover:text-white transition-all duration-300 whitespace-nowrap"
            >
              <span>View All Products</span>
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* Certifications */}
      <AnimatedSection className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-darkgray-500 mb-4">
              Certifications & Compliance
            </h2>
            <p className="text-darkgray-500">Trusted by dental professionals worldwide</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-2xl hover:bg-primary-50 hover:scale-110 transition-all duration-300 cursor-pointer group"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-3 bg-white rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300">
                  <i className={`${cert.icon} text-gold-600 text-3xl`}></i>
                </div>
                <span className="text-sm font-semibold text-darkgray-500 text-center">{cert.name}</span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Testimonials */}
      <AnimatedSection className="py-24 bg-gradient-to-br from-gray-50 to-gold-50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gold-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-gold-300/30 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="inline-block px-4 py-2 bg-primary-50 text-gold-600 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
                Testimonials
              </div>
              <h2 className="text-5xl lg:text-6xl font-display font-bold text-darkgray-500 mb-6">
                What Our Clients Say
              </h2>
              <p className="text-darkgray-500 mb-8 leading-relaxed">
                Trusted by dental professionals across the country for our commitment to excellence and quality craftsmanship.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 text-gold-600 font-semibold hover:text-gold-700 transition-colors duration-300"
              >
                <span>View All Reviews</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="lg:col-span-3">
              <div className="relative">
                <div className="bg-white rounded-3xl shadow-2xl p-10">
                  <div className="flex items-center mb-6">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
                    ))}
                  </div>
                  <p className="text-xl italic text-darkgray-500 mb-8 leading-relaxed">
                    "{testimonials[currentTestimonial].text}"
                  </p>
                  <div className="h-px bg-gray-200 mb-6"></div>
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-3 border-gold-500">
                      <img
                        src={testimonials[currentTestimonial].avatar}
                        alt={testimonials[currentTestimonial].name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <div className="font-bold text-darkgray-500">{testimonials[currentTestimonial].name}</div>
                      <div className="text-sm text-darkgray-500">{testimonials[currentTestimonial].role}</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center space-x-4 mt-8">
                  <button
                    onClick={prevTestimonial}
                    className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gold-500 hover:text-white transition-all duration-300 cursor-pointer"
                    aria-label="Previous testimonial"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <div className="flex space-x-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                          index === currentTestimonial ? 'bg-gold-600 w-8' : 'bg-gray-300'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                      ></button>
                    ))}
                  </div>
                  <button
                    onClick={nextTestimonial}
                    className="w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gold-500 hover:text-white transition-all duration-300 cursor-pointer"
                    aria-label="Next testimonial"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-gradient-to-r from-gray-600 via-darkgray-600 to-gray-700 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <i className="ri-tooth-line absolute top-10 left-20 text-white/10 text-8xl animate-float"></i>
          <i className="ri-contrast-drop-2-line absolute bottom-10 right-20 text-white/10 text-7xl animate-float-slow"></i>
          <i className="ri-service-line absolute top-1/2 left-1/3 text-white/10 text-9xl animate-float"></i>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-display font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Experience the difference of working with a trusted dental laboratory partner. Contact us today for a consultation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/quote"
              className="px-10 py-4 bg-white text-gold-600 rounded-lg font-semibold hover:bg-gold-50 hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gold-600 transition-all duration-300 whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </div>
  );
};

export default HomePage;
